import React, { useCallback, useMemo, useState } from 'react';
import { Popup, getNCFileFromVersions } from '@helpers';
import { NCFileVersion as NCFileVersionType } from '@interfaces/search';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { scale } from '@components/frame';
import { getNCFile } from '@reducers/machines';
import { useGetNCFileById } from '@api/modules/manufacture/hooks';
import { resolveComparisonVersion } from '@components/ncFileVersions/helpers/resolveComparisonVersion';
import NCFileVersionPicker from '@components/ncFileVersions/components/NCFileVersionPicker';
import { PICKER_WIDTH } from '@components/ncFileVersions/components/NCFileVersionPicker/constants';
import { GetNCFileContent, GetNCFileResult } from '@components/ncFileVersions/components/NCFileDiffView/types';
import type { NCFileContentWithVersionProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationNcFilesScreen/NCFileContentWithVersion';
import { replaceVersionInPair } from '@components/ncFileVersions/helpers/replaceVersionInPair';
import { buildVersionLabel } from '@components/ncFileVersions/helpers/buildVersionLabel';
import { sortVersionsBySequence } from '@components/ncFileVersions/helpers/sortVersionsBySequence';
import { showNoVersionsToCompareAlert } from '@components/ncFileVersions/helpers/showNoVersionsToCompareAlert';
import { useNCFileVersionsWithPcsCounts } from '@components/ncFileVersions/hooks/useNCFileVersionsWithPcsCounts';

export interface UseManageNCFileContentWithVersionParams extends Pick<NCFileContentWithVersionProps, 'ncFileId' | 'C'> {}

export const useManageNCFileContentWithVersion = ({ ncFileId, C }: UseManageNCFileContentWithVersionParams) => {
  const dispatch = useAppDispatch();
  const { ncFileData } = useGetNCFileById({ id: ncFileId });

  const rawVersions = useMemo(() => ncFileData?.versions ?? [], [ncFileData?.versions]);
  const versions = useNCFileVersionsWithPcsCounts({ ncFileId: ncFileData?.ncFileId ?? ncFileId, versions: rawVersions });
  const version = useMemo(() => getNCFileFromVersions(versions), [versions]);
  const versionNumber = useMemo(() => buildVersionLabel(version), [version]);

  const [versionsToCompare, setVersionsToCompare] = useState<NCFileVersionType[] | null>(null);

  const fetchNCFileContent = useCallback<GetNCFileContent>(
    (hash, fullFile) => dispatch(getNCFile(hash, fullFile)) as Promise<GetNCFileResult>,
    [dispatch]
  );

  // Opens the version picker so the operator chooses which two versions to diff, instead of jumping into a silent auto-pick.
  const openVersionPicker = useCallback(() => {
    const base = version as NCFileVersionType | null;
    const compareWith = resolveComparisonVersion(versions, base);

    if (!base || !compareWith) {
      showNoVersionsToCompareAlert();
      return;
    }

    // Pre-select the nearest previous version next to the current one (older on the left, newer on the right).
    const initialSelection = sortVersionsBySequence([compareWith, base], true);

    // Content-sized popup (no useFullHeight) so the modal hugs the list; the picker caps its own scroll region.
    Popup({
      Component: (popupProps: { hide?: () => void }) => (
        <NCFileVersionPicker
          {...popupProps}
          C={C}
          ncFileId={ncFileData?.ncFileId ?? ncFileId}
          versions={versions}
          favoriteOrLatest={base}
          initialSelection={initialSelection}
          onCompare={setVersionsToCompare}
        />
      ),
      width: scale(PICKER_WIDTH),
      shouldCloseOnPressOutside: true,
      bg: 'Background/Light/Detail',
    });
  }, [versions, version, C, ncFileData?.ncFileId, ncFileId]);

  const closeComparison = useCallback(() => setVersionsToCompare(null), []);

  // Lets the existing diff header dropdowns swap a compared version after opening.
  const replaceVersionForComparison = useCallback((currentVersionId: number, nextVersion: NCFileVersionType) => {
    setVersionsToCompare((prev) => replaceVersionInPair(prev, currentVersionId, nextVersion));
  }, []);

  return {
    ncFileData,
    versions,
    versionNumber,
    versionsToCompare,
    getNCFile: fetchNCFileContent,
    openVersionPicker,
    closeComparison,
    replaceVersionForComparison,
  };
};
